import type { useHistory } from 'react-router-dom';
import { RefreshResponse } from '@applied/proto/web_services/candidate_experience/proto/authentication_pb';
import type { JsonValue } from '@protobuf-ts/runtime';
import type { MessageType } from '@protobuf-ts/runtime/build/types/message-type';
import { updateAuthenticationCookies } from 'api/cookies';

const refreshResponseType = RefreshResponse as MessageType<RefreshResponse>;

export type ResponseData<T> = {
  statusCode: number;
  jsonPayload: T | null;
  headers: Headers;
};

export class HTTPError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = 'HTTPError';

    Object.setPrototypeOf(this, HTTPError.prototype);
  }
}

export type GenericResponse = { message: string };

export async function postData<TResponse = any>(
  url: string,
  data: any,
): Promise<ResponseData<TResponse>> {
  // Serialize bigint as strings because JSON.stringify does not support bigint.
  const convertBigIntToStrings = (_key: string, value: any): any => {
    if (typeof value === 'bigint') {
      return value.toString();
    }
    return value;
  };

  const response = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(data, convertBigIntToStrings),
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new HTTPError(`POST request failed`, response.status);
  }

  return {
    statusCode: response.status,
    jsonPayload: await response.json(),
    headers: response.headers,
  };
}

/**
 * Gets data from the given URL.
 *
 * @param url The URL to get data from.
 * @returns The response from the server.
 */
export async function getData<TResponse = any>(url: string): Promise<ResponseData<TResponse>> {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new HTTPError(`GET request failed`, response.status);
  }
  return {
    statusCode: response.status,
    jsonPayload: await response.json(),
    headers: response.headers,
  };
}

/**
 * If the authentication request succeeds, either the authentication tokens need to be refreshed
 * or the request was actually served as intended.
 */
export async function handleSuccessfulAuthentication<TResponse extends JsonValue>(
  response: ResponseData<TResponse>,
  request: () => Promise<ResponseData<TResponse>>,
  history: ReturnType<typeof useHistory>,
): Promise<ResponseData<TResponse> | null> {
  try {
    // If the response is of the AuthenticationHeaders type, refresh the authentication tokens
    // and make the request again.
    const updatedTokens = refreshResponseType.fromJson(response.jsonPayload);
    updateAuthenticationCookies(updatedTokens);
    return await makeAuthenticatedRequest(request, history);
  } catch {
    // Otherwise, treat the response as the expected message type.
    return response;
  }
}

/**
 * Makes the given request and handles auth middleware logic.
 *
 * @param request The request to make; should raise an error if the request fails (non-200 response).
 */
export async function makeAuthenticatedRequest<TResponse extends JsonValue>(
  request: () => Promise<ResponseData<TResponse>>,
  history: ReturnType<typeof useHistory>,
): Promise<ResponseData<TResponse> | null> {
  /**
   * This wrapper handles the following cases:
   *   - 200 response: The request resolved in a successful manner (handleSuccessfulAuthentication).
   *      - Authentication tokens are refreshed, automatically resend request.
   *      - Request was served as intended.
   *   - 401 response: Authentication failed; redirect to login page.
   *   - 403 response: Authorization failed; redirect to forbidden page.
   *   - Other response: Loudly fail; application logic should handle.
   */
  try {
    const response = await request();
    return await handleSuccessfulAuthentication(response, request, history);
  } catch (error) {
    if (error.status === 401) {
      history.push('/login');
    } else if (error.status === 403) {
      history.push('/forbidden');
    } else {
      throw error;
    }

    return null;
  }
}

/**
 * Returns true if the request succeeded, false if it raised an error.
 */
export async function didRequestSucceed(request: () => Promise<any>): Promise<boolean> {
  try {
    await request();
    return true;
  } catch (error) {
    return false;
  }
}
